/**
 * Hierarchical TanStack Query keys for PhishTrainer — admin realm AND play realm.
 *
 * NO `mode`, NO `workspaceId`, NO `recipientId` in any key. This mirrors
 * `lib/esa/query-keys.ts` and the rule in apps/web/CLAUDE.md: tenant isolation is
 * enforced by eviction, not by key namespacing —
 *   • admin realm → WorkspaceCacheGuard clears on workspace/company switch
 *   • play realm  → PlayCacheGuard clears on learner-identity change
 * plus the `x-workspace-id` header fetchApi attaches. A key that carries the
 * tenant defeats that design: the guard evicts, but a stale entry under the OLD
 * tenant's key survives in gcTime and can be re-read if the tenant switches back
 * before it expires.
 *
 * ONE FACTORY IN THIS MODULE STILL BREAKS THAT RULE, and this docstring used to
 * claim otherwise. lib/phish-trainer/baseline/query-keys.ts (note the hyphenated
 * dir) keys on `report(workspaceId)`, and lib/phish-trainer/baseline/
 * use-baseline-report-query.ts still calls it — the baseline report page is
 * workspace-keyed to this day. The line here said that factory "folds in here as
 * `reports.baseline()`"; a `reports.baseline()` entry was duly declared, nothing
 * ever consumed it, and the migration it described never happened. A reader who
 * trusted the docstring would have believed the workspace-keyed factory was
 * gone.
 *
 * The dead placeholder is removed rather than left standing, so the two files
 * cannot disagree about where the baseline key lives. Finishing the fold means
 * pointing use-baseline-report-query.ts at a key here and deleting the old
 * factory — and note it also calls getWorkspaceId() during render, so the SSR
 * pass keys on null and the client pass on the real id. Guarded by
 * __tests__/query-keys-baseline-fold.spec.ts.
 *
 * Filters go INTO the key so distinct filter combos cache separately and a
 * targeted `invalidateQueries({ queryKey: ptKeys.<ns>.all() })` refreshes just
 * that namespace instead of forcing a global refetch.
 */

/** Structural, not a strict index signature, so typed filter interfaces pass without a cast. */
type Filters = object | undefined

export const ptKeys = {
  all: () => ['phishtrainer'] as const,

  // ── Admin realm ───────────────────────────────────────────────────────────
  users: {
    all: () => [...ptKeys.all(), 'users'] as const,
    list: (filters?: Filters) => [...ptKeys.users.all(), 'list', filters ?? {}] as const,
    detail: (id: string) => [...ptKeys.users.all(), 'detail', id] as const,
    knowledge: (id: string) => [...ptKeys.users.all(), 'knowledge', id] as const,
    /** Facet values for the roster filter chips. Session-stable. */
    filterOptions: () => [...ptKeys.users.all(), 'filter-options'] as const,
    /** Submitted-round history for one learner. */
    history: (id: string) => [...ptKeys.users.all(), 'history', id] as const,
    /** Points / streak / badge roll-up for one learner. */
    gamification: (id: string) => [...ptKeys.users.all(), 'gamification', id] as const,
    /** Attribution drill-downs behind a pillar / cue score. */
    pillarContributions: (id: string, code: string) =>
      [...ptKeys.users.all(), 'pillar-contributions', id, code] as const,
    cueContributions: (id: string, cue: string) =>
      [...ptKeys.users.all(), 'cue-contributions', id, cue] as const,
  },

  safeAttacks: {
    all: () => [...ptKeys.all(), 'safe-attacks'] as const,
    list: (filters?: Filters) => [...ptKeys.safeAttacks.all(), 'list', filters ?? {}] as const,
    detail: (id: string) => [...ptKeys.safeAttacks.all(), 'detail', id] as const,
    /** Per-source tab counts on the library. */
    counts: () => [...ptKeys.safeAttacks.all(), 'counts'] as const,
  },

  programs: {
    all: () => [...ptKeys.all(), 'programs'] as const,
    list: (filters?: Filters) => [...ptKeys.programs.all(), 'list', filters ?? {}] as const,
    detail: (id: string) => [...ptKeys.programs.all(), 'detail', id] as const,
  },

  reminders: {
    all: () => [...ptKeys.all(), 'reminders'] as const,
    /** Policy options for the program wizard's reminder picker. */
    policyOptions: () => [...ptKeys.all(), 'reminders', 'policy-options'] as const,
    policies: () => [...ptKeys.reminders.all(), 'policies'] as const,
    templates: () => [...ptKeys.reminders.all(), 'templates'] as const,
    queue: (filters?: Filters) => [...ptKeys.reminders.all(), 'queue', filters ?? {}] as const,
  },

  reports: {
    all: () => [...ptKeys.all(), 'reports'] as const,
    list: (filters?: Filters) => [...ptKeys.reports.all(), 'list', filters ?? {}] as const,
    detail: (id: string) => [...ptKeys.reports.all(), 'detail', id] as const,
    // No `baseline()` here on purpose — see the header. The baseline report is
    // still keyed by lib/phish-trainer/baseline/query-keys.ts; add the entry
    // back in the same change that repoints the hook at it, never before.
  },

  /** Org-wide KPI roll-up behind the admin PhishFlag explainer. */
  dashboard: () => [...ptKeys.all(), 'dashboard'] as const,

  engagement: {
    all: () => [...ptKeys.all(), 'engagement'] as const,
    list: (filters?: Filters) => [...ptKeys.engagement.all(), 'list', filters ?? {}] as const,
    byUser: (recipientId: string) => [...ptKeys.engagement.all(), 'user', recipientId] as const,
    byEmail: (safeAttackId: string) => [...ptKeys.engagement.all(), 'email', safeAttackId] as const,
  },

  generations: {
    all: () => [...ptKeys.all(), 'generations'] as const,
    list: (filters?: Filters) => [...ptKeys.generations.all(), 'list', filters ?? {}] as const,
    stats: () => [...ptKeys.generations.all(), 'stats'] as const,
  },

  settings: {
    all: () => [...ptKeys.all(), 'settings'] as const,
    effective: () => [...ptKeys.settings.all(), 'effective'] as const,
  },

  logs: {
    all: () => [...ptKeys.all(), 'logs'] as const,
    audit: (filters?: Filters) => [...ptKeys.logs.all(), 'audit', filters ?? {}] as const,
    email: (filters?: Filters) => [...ptKeys.logs.all(), 'email', filters ?? {}] as const,
  },

  /**
   * The P.H.I.S.H. taxonomy. Session-immutable and read from six independent
   * call sites today; one namespace collapses them to a single cache entry.
   */
  taxonomy: () => [...ptKeys.all(), 'taxonomy'] as const,

  leaderboard: {
    all: () => [...ptKeys.all(), 'leaderboard'] as const,
    list: (filters?: Filters) => [...ptKeys.leaderboard.all(), 'list', filters ?? {}] as const,
  },
} as const

/**
 * PLAY-realm keys, namespaced separately from the admin ones.
 *
 * In the PLAY realm these are deliberately NOT keyed by recipientId: the session
 * token already scopes every response to the caller, and PlayCacheGuard evicts
 * on identity change. Putting the learner in the key there would leave the
 * previous learner's entries resident under their own key — exactly the leak
 * the guard exists to prevent.
 *
 * THAT REASONING DOES NOT HOLD IN THE ADMIN REALM (added 2026-08-30). The same
 * hooks are now called from /phishtrainer with an EXPLICIT userId — the learner
 * being reviewed, not the caller — and PlayCacheGuard is mounted only in
 * app/play/layout.tsx. WorkspaceCacheGuard, which the admin tree does mount,
 * evicts on workspace/company change and cannot know the admin switched which
 * learner they are looking at. With the default 5-minute staleTime and
 * refetchOnWindowFocus off, opening learner A's PF1 and then learner B's PF1
 * served A's rounds, mastery and evidence with NO network request at all — and
 * the shared view renders no learner name, so nothing on screen contradicted it.
 *
 * So: these take an OPTIONAL scope. Supply the learner id wherever the caller
 * knows it (admin); omit it and you get the session-scoped key the play realm
 * relies on. One key factory, both realms, neither leaking into the other.
 */
export const playKeys = {
  all: () => ['play'] as const,

  session: () => [...playKeys.all(), 'session'] as const,
  /**
   * The P.H.I.S.H. framework as the LEARNER fetches it.
   *
   * Separate from ptKeys.taxonomy() on purpose, even though the content is the
   * same global seeded taxonomy. The two realms reach it through different
   * endpoints with different envelopes — the admin route returns the framework
   * directly, the play route wraps it in {data} — and different auth. Sharing
   * one key would let whichever realm mounted first populate the entry, so the
   * other would render from cache without ever calling its own endpoint. Same
   * data, but a broken play-auth path would look healthy.
   */
  framework: () => [...playKeys.all(), 'framework'] as const,
  settings: () => [...playKeys.all(), 'settings'] as const,
  dashboard: () => [...playKeys.all(), 'dashboard'] as const,
  knowledge: () => [...playKeys.all(), 'knowledge'] as const,
  preferences: () => [...playKeys.all(), 'preferences'] as const,

  library: {
    all: () => [...playKeys.all(), 'library'] as const,
    list: (filters?: Filters, scope?: string) =>
      [...playKeys.library.all(), 'list', filters ?? {}, scope ?? 'self'] as const,
  },

  rounds: {
    all: () => [...playKeys.all(), 'rounds'] as const,
    history: (filters?: Filters) => [...playKeys.rounds.all(), 'history', filters ?? {}] as const,
    detail: (id: string) => [...playKeys.rounds.all(), 'detail', id] as const,
    feedback: (id: string) => [...playKeys.rounds.all(), 'feedback', id] as const,
  },

  mailbox: (filters?: Filters) => [...playKeys.all(), 'mailbox', filters ?? {}] as const,
  leaderboard: (filters?: Filters) => [...playKeys.all(), 'leaderboard', filters ?? {}] as const,

  contributions: {
    all: () => [...playKeys.all(), 'contributions'] as const,
    pillar: (code: string, scope?: string) =>
      [...playKeys.contributions.all(), 'pillar', code, scope ?? 'self'] as const,
    cue: (cue: string, scope?: string) =>
      [...playKeys.contributions.all(), 'cue', cue, scope ?? 'self'] as const,
  },
} as const
